import * as go from 'gojs/release/go-module';
import * as React from 'react';

import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { ModelConceptRelationship } from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addMappingsToConnectedValidRelationships } from 'src/containers/ProjectDiagram/utils/mapping/addMappingsToConnectedValidRelationships';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addDomainOrRangeToRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addDomainOrRangeToRelationship';
import { removeModelConceptRelationshipForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationshipForClass';
import { setPropertyType } from 'src/containers/ProjectDiagram/utils/propertyType/setPropertyType';

export const addRelationshipItemToClass =
  (classIri: string, item: ClassRelationshipItem) =>
  (model: Model, diagram: ExtendedDiagram) => {
    const allRelationships: ModelConceptRelationship[] = [];
    item.directions.forEach((direction) => {
      const { addedRelationships, reconnectedRelationships } =
        addDomainOrRangeToRelationship({
          direction,
          classIri,
          relationshipIri: item.value,
          useAllValidLinks: true,
        })(model, diagram);
      allRelationships.push(...addedRelationships, ...reconnectedRelationships);
    });

    addMappingsToConnectedValidRelationships(allRelationships)(model);

    if (item.isNewlyCreated) {
      setLabelForIri(item.value, item.text)(model);
      setPropertyType(item.value)(model);
    }
  };

export const useAddRelationshipForClass = ({
  classIri,
}: {
  classIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: ClassRelationshipItem | null) => {
      if (!item || !diagram || !classIri) {
        return;
      }

      if (item.isNewlyCreated && !item.text) {
        return;
      }

      commitDiagramModel(diagram, addRelationshipItemToClass(classIri, item));
    },
    [diagram, classIri]
  );
};

export const useRemoveRelationshipForClass = ({
  classIri,
}: {
  classIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: ClassRelationshipItem | null) => {
      if (!item || !diagram || !classIri) {
        return;
      }

      const { value } = item;

      commitDiagramModel(
        diagram,
        removeModelConceptRelationshipForClass({
          direction: null,
          iri: value,
          classIri,
        })
      );
    },
    [diagram, classIri]
  );
};

export const useUpdateRelationshipForClass = ({
  classIri,
}: {
  classIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: ClassRelationshipItem | null) => {
      if (!item || !diagram || !classIri) {
        return;
      }

      const { value, directions, text } = item;
      if (!text) {
        return;
      }

      const classKey = getModelConceptClassKey(classIri);

      const directionsToAdd: RelationshipDirection[] = [];
      const directionsToRemove: RelationshipDirection[] = [];
      const layoutPartial = new Set<go.Key>();

      const addDirection = (direction: RelationshipDirection) => {
        const current = diagram.findLinksByExample({
          value,
          category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
          [direction === RelationshipDirection.OUTGOING ? 'from' : 'to']:
            classKey,
        });
        const hasCurrent = Boolean(new go.Set(current).size);
        const hasNext = directions.includes(direction);

        if (!hasCurrent && hasNext) {
          directionsToAdd.push(direction);
        } else if (hasCurrent && !hasNext) {
          directionsToRemove.push(direction);
          current.each((link) => {
            layoutPartial.add(link.data.key);
            link.labelNodes.each((labelNode) => {
              layoutPartial.add(labelNode.data.key);
            });
            if (link.data.to) {
              layoutPartial.add(link.data.to);
            }
            if (link.data.from) {
              layoutPartial.add(link.data.from);
            }
          });
        }
      };

      addDirection(RelationshipDirection.OUTGOING);
      addDirection(RelationshipDirection.INCOMING);

      commitDiagramModel(
        diagram,
        (model) => {
          directionsToRemove.forEach((direction) => {
            removeModelConceptRelationshipForClass({
              direction,
              iri: value,
              classIri,
            })(model);
          });

          const allRelationships: ModelConceptRelationship[] = [];
          directionsToAdd.forEach((direction) => {
            const { addedRelationships, reconnectedRelationships } =
              addDomainOrRangeToRelationship({
                direction,
                classIri,
                relationshipIri: value,
                useAllValidLinks: true,
              })(model, diagram);
            allRelationships.push(
              ...addedRelationships,
              ...reconnectedRelationships
            );
          });

          addMappingsToConnectedValidRelationships(allRelationships)(model);

          setLabelForIri(value, text)(model);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
    },
    [diagram, classIri]
  );
};
